import React, { useContext, useEffect, useMemo, useState } from "react"
import { useMutation, useQuery } from "@tanstack/react-query"

import { Button, Col, Divider, Flex, Form, Grid, Input, Popconfirm, Row, Select, Skeleton, Space, Tooltip, Typography, notification } from "antd"

import { PropriedadesDeNotificacao } from "../../../Utils/Notificacoes"
import { PropriedadesDoPopConfirm } from "../../../Defaults"
import { TypeUsuario } from "../../../Models/Usuario"
import { UsuarioService } from "../../../Services/UsuarioService"
import { SessaoContext } from "../../../Contexts/SessaoContext"

import { Permissoes } from "../../../Constants/Permissoes"
import { Restrito } from "../../../Components/Restrito"

import { ResponsiveAntdTable } from "../../../Components/ResponsiveAntdTable";
import { Conjunto } from "../../../Components/Conjunto";
import { Ordenar } from "../../../Utils/Ordenacao";

import { Link, matchPath, Route, Routes, useNavigate } from "react-router-dom";
import { useRelativePath } from "../../../Hooks/useNextRelativePath";
import { useRedirection } from "../../../Hooks/useRedirection";
import { useWatchParameterOnRoutes } from "../../../Hooks/useGetParameterOnRoutes";

import AdicionarAjuda, { ITutorial } from "../../../Components/AdicionarAjuda"
import * as Tutoriais from "./usuario.tutorial"
import { ehEmail } from "../../../Utils/Formatacao"

type UsuarioSemSenha = Omit<TypeUsuario, "senha"> ;

const ListagemDeUsuarios: React.FC<{
    aoAdicionar?: () => any,
    aoEditar?: (usuario: UsuarioSemSenha) => any,

    hrefDoBotaoAdicionar?: string,
    hrefDoBotaoEditar?: (usuario: TypeUsuario) => string | void
}> = (props) => {

    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);
    const tamanhoDaTela = Grid.useBreakpoint();

    interface IFiltro {
        nome?: string,
        usuario?: string,
        email?: string,
        ativo?: boolean,
    }

    const [filtro, setFiltro] = useState<IFiltro>({})

    const usuarioService = new UsuarioService();
    const usuariosDisponiveis = useQuery({
        queryKey: ["listarUsuarios", filtro],
        gcTime: 0,
        queryFn: () => usuarioService.listarUsuarios({
            nome: filtro.nome,
            usuario: filtro.usuario,
            email: filtro.email,
            ativo: filtro.ativo,
        }),
        enabled: true,
        refetchOnWindowFocus: false
    })

    const adicionar = permissoes?.includes(Permissoes.CriarUsuario);

    return (
        <React.Fragment>
            <Form<UsuarioSemSenha>
                layout="vertical"
                autoComplete="off"
                onFinish={(_filtro) => {
                    setFiltro(_filtro);
                }}
            >
                <Row gutter={16} id={Tutoriais.IDs.formularioDeFiltro} wrap>
                    <Col xs={12} md={5} id={Tutoriais.IDs.filtroDeNome}>
                        <Form.Item<UsuarioSemSenha> name={"nome"} label={"Nome"}>
                            <Input placeholder="Todos" />
                        </Form.Item>
                    </Col>

                    <Col xs={12} md={6} id={Tutoriais.IDs.filtroDeEmail}>
                        <Form.Item<UsuarioSemSenha> name={"email"} label={"Email"}>
                            <Input placeholder="Todos" />
                        </Form.Item>
                    </Col>

                    <Col xs={12} md={5} id={Tutoriais.IDs.filtroDeUsuario}>
                        <Form.Item<UsuarioSemSenha> name={"usuario"} label={"Usuário"}>
                            <Input placeholder="Todos" />
                        </Form.Item>
                    </Col>

                    <Col xs={12} md={3} id={Tutoriais.IDs.filtroDeAtivo}>
                        <Form.Item<UsuarioSemSenha> name="ativo" label="Ativo" initialValue={true}>
                            <Select
                                allowClear
                                placeholder={"Todos"}
                                options={[
                                    { value: true, label: "Ativo" },
                                    { value: false, label: "Inativo" },
                                ]}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={adicionar ? 12 : 24} md={adicionar ? 2 : 5} style={{ alignItems: "end", display: "flex" }}>
                        <Form.Item<UsuarioSemSenha> style={{ flexGrow: 1 }}>
                            <Button
                                type="primary"
                                key="submit"
                                htmlType="submit"
                                id={Tutoriais.IDs.botaoListar}
                                style={{ width: "100%" }}
                                disabled={usuariosDisponiveis.isFetching}
                            >
                                Listar
                            </Button>
                        </Form.Item>
                    </Col>

                    <Restrito aberto={adicionar}>
                        <Col xs={12} md={3} id={Tutoriais.IDs.botaoAdicionar} style={{ alignItems: "end", display: "flex" }}>
                            <Form.Item<UsuarioSemSenha> style={{ flexGrow: 1 }}>
                                <Link
                                    to={props.hrefDoBotaoAdicionar ?? "#"}
                                    onClick={(e) => {
                                        !props.hrefDoBotaoAdicionar && e.preventDefault();
                                    }}
                                >
                                    <Button
                                        type="primary"
                                        onClick={() => {
                                            props.aoAdicionar?.();
                                        }}
                                        style={{ width: "100%" }}
                                    >
                                        Adicionar
                                    </Button>
                                </Link>
                            </Form.Item>
                        </Col>
                    </Restrito>
                </Row>
            </Form>

            <Row>
                <Col xs={24}>
                    <ResponsiveAntdTable
                        bordered
                        locale={{ emptyText: "Nenhum usuário encontrado." }}
                        dataSource={usuariosDisponiveis.data?.data}
                        loading={!usuariosDisponiveis.isFetched}
                        rowKey={(k) => k.codigo}
                        addTitleOn="lg"
                        columns={[
                            {
                                title: "Código",
                                dataIndex: "codigo",
                                breakpoints: { xs: 12, sm: 12, lg: 2 },
                                sorter: (a, b) => Ordenar.Numero(a.codigo, b.codigo),
                                key: "codigo",
                            },
                            {
                                title: "Ativo",
                                dataIndex: "ativo",
                                breakpoints: { xs: 12, sm: 12, lg: 2 },
                                sorter: (a, b) => Ordenar.Booleano(a.ativo, b.ativo),
                                render: (v) => (v ? "Sim" : "Não"),
                                key: "ativo",
                            },
                            {
                                title: "Usuário",
                                dataIndex: "usuario",
                                breakpoints: { xs: 12, sm: 8, lg: 4 },
                                key: "usuario",
                                sorter: (a, b) => Ordenar.String(a.usuario, b.usuario),
                            },
                            {
                                title: "Nome",
                                dataIndex: "nome",
                                breakpoints: { xs: 12, sm: 8, lg: 4 },
                                key: "nome",
                                sorter: (a, b) => Ordenar.String(a.nome, b.nome),
                            },
                            {
                                title: "Email",
                                dataIndex: "email",
                                breakpoints: { xs: 24, sm: 8, lg: 7 },
                                sorter: (a, b) => Ordenar.String(a.email, b.email),
                                key: "email",
                            },

                            {
                                title: "Ações",
                                key: "acoes",
                                noResponsiveTitle: true,
                                breakpoints: { xs: 24, sm: 24, lg: 5 },
                                hidden: [Permissoes.AtualizarUsuario, Permissoes.DesativarUsuario, Permissoes.AtivarUsuario].every(
                                    (p) => !permissoes?.includes(p)
                                ),
                                render: (_, item) => {
                                    return (
                                        <Conjunto
                                            style={{ justifyContent: tamanhoDaTela.sm ? "unset" : "center", display: "flex", flexWrap: "wrap" }}
                                            itens={(() => {
                                                if (item.ativo) {
                                                    return [
                                                        permissoes?.includes(Permissoes.AtualizarUsuario) && (
                                                            <Link
                                                                to={props.hrefDoBotaoEditar?.(item) ?? "#"}
                                                                onClick={(e) => !props.hrefDoBotaoEditar && e.preventDefault()}
                                                            >
                                                                <Button
                                                                    className={Tutoriais.Classes.botaoEditar}
                                                                    type="primary"
                                                                    size="small"
                                                                    onClick={() => {
                                                                        props.aoEditar?.(item);
                                                                    }}
                                                                >
                                                                    Editar
                                                                </Button>
                                                            </Link>
                                                        ),
                                                        permissoes?.includes(Permissoes.DesativarUsuario) && (
                                                            <Popconfirm
                                                                {...PropriedadesDoPopConfirm("desativar")}
                                                                onConfirm={() => {
                                                                    if (item.codigo) {
                                                                        usuarioService.desativarUsuario(item.codigo).then(() => {
                                                                            usuariosDisponiveis.refetch();
                                                                        });
                                                                    }
                                                                }}
                                                            >
                                                                <Button type="default" danger size="small" className={Tutoriais.Classes.botaoDesativar}>
                                                                    Desativar
                                                                </Button>
                                                            </Popconfirm>
                                                        ),
                                                    ];
                                                } else {
                                                    return [
                                                        permissoes?.includes(Permissoes.AtivarUsuario) && (
                                                            <Popconfirm
                                                                {...PropriedadesDoPopConfirm("ativar")}
                                                                onConfirm={() => {
                                                                    if (item.codigo) {
                                                                        usuarioService.ativarUsuario(item.codigo).then(() => {
                                                                            usuariosDisponiveis.refetch();
                                                                        });
                                                                    }
                                                                }}
                                                            >
                                                                <Button type="dashed" size="small">
                                                                    Reativar
                                                                </Button>
                                                            </Popconfirm>
                                                        ),
                                                    ];
                                                }
                                            })()}
                                        />
                                    );
                                },
                            },
                        ]}
                        
                        csv={{
                            enabled: true,
                            filename: "usuarios.csv",
                        }}
                    />
                </Col>
            </Row>

            <AdicionarAjuda
                tutoriais={
                    [
                        {
                            titulo: "Como consultar usuários",
                            tutorial: Tutoriais.ConsultarUsuario,
                        },

                        permissoes?.includes(Permissoes.CriarUsuario) && {
                            titulo: "Como cadastrar um novo usuário",
                            tutorial: Tutoriais.AdicionarUsuario,
                        },

                        permissoes?.includes(Permissoes.AtualizarUsuario) && {
                            titulo: "Como editar um usuário",
                            tutorial: Tutoriais.EditarUsuario,
                        },

                        permissoes?.includes(Permissoes.DesativarUsuario) && {
                            titulo: "Como desativar um usuário",
                            tutorial: Tutoriais.DesativarUsuario,
                        },
                    ].filter((v) => v != false) as ITutorial[]
                }
            />
        </React.Fragment>
    );
}

const CadastroDeUsuarios: React.FC<{
    aoCancelar: () => any,
    depoisDeFinalizar?: () => any,
    usuarioEmEdicao?: UsuarioSemSenha,
}> = (props) => {

    const usuario = useContext(SessaoContext)?.sessao?.usuario;
    const { usuarioEmEdicao } = props;

    const usuarioService = new UsuarioService();

    interface IFormularioDeCadastroDeUsuarios {
        nome: string,
        nomeSocial?: string,
        usuario: string,
        email: string
    }

    const estaEditando = useMemo(() => {
        return usuarioEmEdicao !== undefined;
    }, [usuarioEmEdicao])

    const aplicarModificacoes = (valores: IFormularioDeCadastroDeUsuarios) => {
        if (estaEditando) {
            if (usuarioEmEdicao?.codigo === undefined) return;

            usuarioService.atualizarUsuario({ ...valores, perfis: [], permissoes: [], codigo: usuarioEmEdicao.codigo })
                .then((resposta) => {
                    notification.open({
                        ...PropriedadesDeNotificacao.Sucesso("Usuário atualizado com sucesso.")
                    });
                    
                    props.depoisDeFinalizar?.();
                })
                .catch(() => {})
        } else {
            usuarioService.inserirUsuario({ ...valores, perfis: [], permissoes: []})
                .then((resposta) => {
                    notification.open({
                        ...PropriedadesDeNotificacao.Sucesso((
                            <div>
                                Usuário adicionado com sucesso. <br />
                                Uma senha aleatória temporária foi gerada e será enviada para o e-mail referido.
                            </div>
                        ))
                    });

                    props.depoisDeFinalizar?.();
                })
                .catch(() => {})
        }
    };

    return <React.Fragment>
        <Form<IFormularioDeCadastroDeUsuarios> layout="vertical" validateTrigger="onBlur" autoComplete="off"
            onFinish={aplicarModificacoes}
        >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>

                <Row gutter={16}>
                    <Col span={16}>
                        <Form.Item<UsuarioSemSenha>
                            label="Nome"
                            name="nome"
                            initialValue={usuarioEmEdicao?.nome}
                            rules={[{ required: true, message: "O nome é obrigatório." }]}
                        >
                            <Input placeholder="Nome" />
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item<UsuarioSemSenha>
                            name="nomeSocial"
                            label="Apelido"
                            initialValue={usuarioEmEdicao?.nomeSocial}
                        >
                            <Input disabled placeholder="Apelido" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item<UsuarioSemSenha>
                            label="Nome de usuário"
                            name="usuario"
                            initialValue={usuarioEmEdicao?.usuario}
                            rules={[
                                {
                                    required: true,
                                    validator: async (_, val: string) => {
                                        if (val == null || val.trim() == "")
                                            return Promise.reject("O nome de usuário é obrigatório")

                                        if (!val.match(/^[\w\._]+$/))
                                            return Promise.reject("O nome de usuário deve ser formado apenas por caractéres alfanuméricos ou '.' ou '_'.")

                                        if (val.length < 5 || val.length > 30)
                                            return Promise.reject("O nome de usuário deve ter entre 5 a 30 caractéres em tamanho.")

                                        if (!estaEditando) {
                                            const disponibilidade = await usuarioService.verificarDisponibilidade({
                                                nomeDeUsuario: val
                                            })

                                            if (!disponibilidade.data.nomeDeUsuario) {
                                                return Promise.reject("Nome de usuário já em uso.")
                                            }
                                        }

                                    }
                                }
                            ]}>
                            <Input placeholder="Nome de usuário" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item<UsuarioSemSenha>
                            label="E-mail"
                            name="email"
                            initialValue={usuarioEmEdicao?.email}
                            rules={[
                                {
                                    required: true,
                                    validator: async (_, val: string) => {
                                        if (val == null || val.trim() == "")
                                            return Promise.reject("O campo e-mail é obrigatório");

                                        if (!ehEmail(val)) {
                                            return Promise.reject("Insira um e-mail válido");
                                        }
                                        
                                        if (!estaEditando) {
                                            const disponibilidade = await usuarioService.verificarDisponibilidade({
                                                email: val
                                            })

                                            if (!disponibilidade.data.email) {
                                                return Promise.reject("Email já em uso.")
                                            }
                                        }

                                    },
                                },
                            ]}
                        >
                            <Input placeholder="E-mail" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Flex align="center" justify="center" gap={16}>
                            <Button danger type="primary" key="cancel" onClick={() => {
                                props.aoCancelar();
                            }}>
                                Cancelar
                            </Button>

                            <Button type="primary" key="submit" htmlType='submit' 
                                disabled={!usuario?.permissoes.map((p) => p.nome).includes(props.usuarioEmEdicao != undefined? Permissoes.AtualizarUsuario : Permissoes.CriarUsuario)}
                            >
                                Salvar
                            </Button>
                        </Flex>
                    </Col>

                </Row>

            </Space>

        </Form>
    </React.Fragment>
}

export const Usuario: React.FC<{}> = (props) => {
    const [usuarioEmEdicao, setUsuarioEmEdicao] = useState<TypeUsuario | undefined>(undefined);
    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);

    const navigate = useNavigate();
    const [raiz, subrota] = useRelativePath();

    const rotaDeEdicao = matchPath({ path: "/editar" }, subrota);
    const rotaDeVisualizacao = matchPath({ path: "/detalhes" }, subrota);

    const obterUsuario = useMutation({
        mutationFn: (id: number) => new UsuarioService().listarUsuarios({ codigo: id, ativo: true }),
        onSuccess: (resposta) => {
            const resultados = resposta.data;
            if (resultados.length == 0) {
                notification.open({
                    ...PropriedadesDeNotificacao.Erro("Nenhum registro foi encontrado para o identificador informado")
                })
                navigate(raiz)
            } else {
                setUsuarioEmEdicao(resposta.data.at(0))
            }
        }
    })

    useRedirection([Permissoes.ListarUsuarios, Permissoes.CriarUsuario, Permissoes.AtualizarUsuario, Permissoes.DesativarUsuario])
    useWatchParameterOnRoutes({
        appliablePaths: [rotaDeEdicao, rotaDeVisualizacao], paramToWatch: "id", filters: [(p) => isNaN(Number(p))],
        onNotAccepted() { navigate(raiz) },
        onExists(param) { obterUsuario.mutate(Number(param)); },
    })

    return (
        <>
            <h2 style={{ marginTop: 0 }}>
                {
                    rotaDeVisualizacao ? "Detalhes do Usuario" : rotaDeEdicao ? "Edição do Usuario" : "Cadastro de Usuario"
                }
            </h2>

            <Divider />

            <Routes>
                {
                    permissoes?.includes(Permissoes.ListarUsuarios) &&
                    <Route path="*" index element={
                        <ListagemDeUsuarios
                            hrefDoBotaoAdicionar={raiz + "/cadastro"}
                            hrefDoBotaoEditar={(item) => {
                                if (item.codigo) {
                                    return raiz + `/editar?id=${item.codigo}`
                                }
                            }}

                            aoEditar={(usuario) => {
                                setUsuarioEmEdicao(usuario);
                            }}
                        />
                    } />
                }

                {
                    permissoes?.includes(Permissoes.CriarUsuario) &&
                    <Route path="/cadastro" element={
                        <CadastroDeUsuarios
                            aoCancelar={() => { navigate(raiz) }}
                            depoisDeFinalizar={() => { navigate(raiz)}}
                        />
                    } />
                }

                {
                    permissoes?.includes(Permissoes.AtualizarUsuario) &&
                    <Route path="/editar" element={
                        usuarioEmEdicao == undefined ? <Skeleton active /> :
                            <CadastroDeUsuarios
                                usuarioEmEdicao={usuarioEmEdicao}
                                aoCancelar={() => { navigate(raiz) }}
                                depoisDeFinalizar={() => { navigate(raiz) }}
                            />
                    } />
                }
            </Routes>


        </>
    );
}
